<template>
  <b-form class="payment-form form">
    <div class="form__section mb-0">
      <b-row>
        <b-col>
          <b-form-group>
            <custom-input
              ref="cardNumber"
              :id="getStripeElementId('card-number')"
              type="number"
              required
              autofocus
              placeholder="Card Number"
              component="div"
              :is-stripe="true"
            ></custom-input>
          </b-form-group>
        </b-col>
      </b-row>
      <b-row>
        <b-col cols="7">
          <b-form-group>
            <custom-input
              ref="cardExpiry"
              :id="getStripeElementId('card-expiry')"
              type="number"
              required
              autofocus
              placeholder="Expiry"
              component="div"
              :is-stripe="true"
            ></custom-input>
          </b-form-group>
        </b-col>
        <b-col cols="5">
          <b-form-group>
            <custom-input
              ref="cardCvc"
              :id="getStripeElementId('card-cvc')"
              type="number"
              required
              autofocus
              placeholder="CVC"
              component="div"
              :is-stripe="true"
            ></custom-input>
          </b-form-group>
        </b-col>
      </b-row>
    </div>

    <error-message
      class="mb-2"
      sign=""
      :error="
        state.$getError('cardNumber') ||
          state.$getError('cardExpiry') ||
          state.$getError('cardCvc') ||
          state.$getError('source') ||
          state.$errorMessage
      "
    ></error-message>

    <div v-if="hasCreate" class="modal-footer">
      <div></div>
      <div class="modal__btns">
        <b-button variant="outline-secondary" @click="onClose"
          >Discard</b-button
        >
        <b-button
          class="btn-action"
          variant="primary"
          :disabled="state.$busy"
          @click="onSubmit"
        >
          <b-spinner small v-if="state.$busy"></b-spinner>&nbsp;Save
        </b-button>
      </div>
    </div>
  </b-form>
</template>
<script>
/**
 * ==================================================================================
 * Payment Form
 * ==================================================================================
 **/

import { mapActions } from 'vuex'
import Form from '@/utils/form'
import StripeElements from '@/utils/mixins/stripeElements'
import CustomInput from '@/components/inputs/CustomInput'
import ErrorMessage from '@/components/inputs/ErrorMessage'

export default {
  components: {
    CustomInput,
    ErrorMessage
  },

  props: {
    /**
     * Used for Stripe element IDs in case of component
     * being used on the same page
     */
    suffix: {
      type: String,
      default: ''
    },
    hasCreate: {
      type: Boolean,
      default: true
    }
  },

  mixins: [StripeElements],

  data: function() {
    return {
      state: new Form({}),
      stripe: null,
      element: null,
      cardNumber: null,
      cardExpiry: null,
      cardCvc: null
    }
  },

  methods: {
    ...mapActions({
      savePayment: 'payment/savePayment',
      addAlert: 'alert/addAlert'
    }),

    setupStripe() {
      this.initStripe(this.$env.stripePublishableKey)

      this.cardNumber = this.createStripeElement(
        'cardNumber',
        this.getStripeElementId('card-number'),
        this.$refs.cardNumber,
        this.onError
      )
      this.cardExpiry = this.createStripeElement(
        'cardExpiry',
        this.getStripeElementId('card-expiry'),
        this.$refs.cardExpiry,
        this.onError
      )
      this.cardCvc = this.createStripeElement(
        'cardCvc',
        this.getStripeElementId('card-cvc'),
        this.$refs.cardCvc,
        this.onError
      )
    },

    async onSubmit() {
      if (this.state.$busy) return
      this.disable(true)

      return new Promise((resolve, reject) => {
        this.stripe
          .createToken(this.cardNumber, this.cardExpiry, this.cardCvc)
          .then(result => {
            if (result.error) {
              if (result.error.type !== 'validation_error') {
                this.state.$errorMessage = result.error.message
              }

              if (!this.hasCreate) {
                reject(this.state.$errorMessage)
              }

              this.disable(false)
            } else {
              if (!this.hasCreate) {
                resolve(result)
              } else {
                this.submitPayment(result.token.id)
              }
            }
          })
      })
    },

    async submitPayment(token) {
      this.disable(true)

      await this.savePayment({
        source: token
      })
        .then(data => {
          this.reset()
          this.addAlert({
            label: 'Payment method has been added'
          })

          this.$emit('on-success', data.data)
        })
        .catch(error => {
          this.state.$handleError(error)
          this.disable(false)
        })
    },

    onError(name, error) {
      if (error) {
        this.state.$setError(name, error)
      } else {
        this.state.$clearError(name)
      }
    },

    onClose() {
      this.$emit('on-close')
    },

    reset() {
      this.disable(false)
      this.cardNumber.clear()
      this.cardExpiry.clear()
      this.cardCvc.clear()
    },

    disable(bool) {
      this.state.$setLoading(bool)
      this.cardNumber.update({ disabled: bool })
      this.cardExpiry.update({ disabled: bool })
      this.cardCvc.update({ disabled: bool })
    },

    getStripeElementId(cls) {
      return this.suffix ? `${cls}-${this.suffix}` : cls
    }
  },

  mounted() {
    this.$nextTick(() => {
      this.setupStripe()
    })
  }
}
</script>
<style lang="scss" scoped>
.payment-form {
  .modal-footer {
    padding-top: 20px;
    justify-content: space-between;

    .btn {
      line-height: 27px;
      min-width: 125px;
      padding: 0.6rem 0.75rem;
    }
  }
}
</style>
